/**
 * Row shapes, as the admin actually consumes them.
 *
 * Every page in this panel used `any[]`, which is why a renamed column or a
 * failed join surfaced as `undefined` in the UI rather than as a build error.
 * These are hand-written rather than generated because the panel reads through
 * `admin_*` RPCs (migration 0008) whose shapes are defined by us, not by the
 * table.
 *
 * Where a type says `| null`, the column really is nullable — don't "fix" it
 * by widening at the call site.
 */

/**
 * The app's status vocabulary, which is the real one.
 *
 * This panel previously used `completed` where the app uses `shipped` and
 * `delivered`. The app looks each status up in a colour map and falls back to
 * pending's amber for anything missing, so an order marked "completed" here
 * showed the customer an amber badge reading "Completed" — and neither of the
 * two statuses they care about could be set at all.
 *
 * `admin_set_order_status` (migration 0009) rejects anything outside this set,
 * so adding a value here without adding it there will fail at the database.
 */
export type OrderStatus =
  | "pending"
  | "processing"
  | "shipped"
  | "delivered"
  | "cancelled";

export const ORDER_STATUSES: OrderStatus[] = [
  "pending",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
];

/** One row of the Users table. Comes from `admin_users`. */
export type AdminUser = {
  id: string;
  full_name: string | null;
  username: string | null;
  email: string | null;
  avatar_url: string | null;
  points: number | null;
  is_premium: boolean;
  premium_plan: string | null;
  is_admin: boolean;
  /** Set by an admin. The account is blocked. */
  suspended_at: string | null;
  /** Set by the user. They asked for deletion and are inside the grace window. */
  deactivated_at: string | null;
  /** Already decayed server-side — a lapsed streak reads 0, not its old value. */
  streak_count: number;
  posts_count: number;
  orders_count: number;
  created_at: string;
  /** Total matching rows before the page limit, for pagination. */
  total_count: number;
};

export type UserFilter =
  | "all"
  | "premium"
  | "free"
  | "suspended"
  | "deleting"
  | "admins";

/** The detail drawer payload. Comes from `admin_user_detail` as one jsonb blob. */
export type AdminUserDetail = {
  profile: {
    id: string;
    full_name: string | null;
    username: string | null;
    email: string | null;
    phone: string | null;
    gender: string | null;
    avatar_url: string | null;
    points: number | null;
    is_premium: boolean;
    premium_plan: string | null;
    premium_expires_at: string | null;
    stripe_customer_id: string | null;
    is_admin: boolean;
    suspended_at: string | null;
    suspended_reason: string | null;
    deactivated_at: string | null;
    deletion_reason: string | null;
    onboarding_completed: boolean | null;
    guide_seen_at: string | null;
    hair_profile: Record<string, unknown> | null;
    currency: string | null;
    streak_count: number | null;
    streak_best: number | null;
    streak_last_active_on: string | null;
    created_at: string;
  };
  billing_address: Record<string, string> | null;
  live_streak: number;
  orders: {
    id: string;
    status: OrderStatus;
    total_amount: number | null;
    currency: string | null;
    created_at: string;
  }[];
  posts: {
    id: string;
    content: string | null;
    likes_count: number;
    comments_count: number;
    has_media: boolean;
    created_at: string;
  }[];
  zora_usage: { message_count: number; next_reset: string } | null;
  admin_actions: {
    action: string;
    detail: Record<string, unknown>;
    created_at: string;
    admin: string | null;
  }[];
};

export type AdminAction = {
  id: string;
  admin_id: string;
  action: string;
  target_table: string | null;
  target_id: string | null;
  detail: Record<string, unknown>;
  created_at: string;
};
